import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { getPagePopupById, createPagePopup, updatePagePopup, getUsers } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const PopupForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        image: null,
        targetUsers: 'all',
        userIds: [],
        status: 'active',
        displayFrom: '',
        displayUntil: ''
    });
    const [imagePreview, setImagePreview] = useState('');

    useEffect(() => {
        const initializeData = async () => {
            await fetchUsers();
            if (id) {
                fetchPopupDetails();
            }
        };
        initializeData();
    }, [id]);

    const fetchUsers = async () => {
        try {
            const response = await getUsers({ page: 1, limit: 1000 });
            if (response.success && response.data) {
                setUsers(response.data.users || response.data || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchPopupDetails = async () => {
        try {
            setLoading(true);
            const response = await getPagePopupById(id);
            if (response.success && response.data) {
                const popup = response.data;
                
                // Format selected users from the nested user objects
                let selectedUserIds = [];
                if (popup.targetUsers === 'specific' && popup.userIds?.length > 0) {
                    selectedUserIds = popup.userIds.map(user => 
                        typeof user === 'object' ? user._id : user
                    );
                }

                setFormData({
                    title: popup.title || '',
                    content: popup.content || '',
                    targetUsers: popup.targetUsers || 'all',
                    userIds: selectedUserIds,
                    status: popup.status || 'active',
                    displayFrom: popup.displayFrom ? new Date(popup.displayFrom).toISOString().split('T')[0] : '',
                    displayUntil: popup.displayUntil ? new Date(popup.displayUntil).toISOString().split('T')[0] : ''
                });
                
                if (popup.image) {
                    const baseUrl = process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
                    let imgPath = popup.image.replace(/\\/g, '/');
                    if (!imgPath.startsWith('/')) {
                        imgPath = `/${imgPath}`;
                    }
                    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                    setImagePreview(`${cleanBaseUrl}${imgPath}`);
                }
            }
        } catch (error) {
            console.error('Error fetching popup details:', error);
            alert(error.message || 'Failed to fetch popup details');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                image: file
            }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleRemoveImage = () => {
        setFormData(prev => ({
            ...prev,
            image: null
        }));
        setImagePreview('');
    };

    const handleUserToggle = (userId) => {
        setFormData(prev => {
            const userIds = prev.userIds.includes(userId)
                ? prev.userIds.filter(id => id !== userId)
                : [...prev.userIds, userId];
            return {
                ...prev,
                userIds
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setProcessing(true);

            const data = new FormData();
            data.append('title', formData.title);
            data.append('content', formData.content);
            data.append('targetUsers', formData.targetUsers);
            data.append('status', formData.status);
            data.append('displayFrom', formData.displayFrom);
            
            if (formData.displayUntil) {
                data.append('displayUntil', formData.displayUntil);
            }

            if (formData.image instanceof File) {
                data.append('image', formData.image);
            }

            if (formData.targetUsers === 'specific' && formData.userIds.length > 0) {
                formData.userIds.forEach(userId => {
                    data.append('userIds[]', userId);
                });
            }

            const response = id
                ? await updatePagePopup(id, data)
                : await createPagePopup(data);

            if (response.success) {
                alert(`Popup ${id ? 'updated' : 'created'} successfully`);
                navigate('/admin/page-popups');
            } else {
                throw new Error(response.message || `Failed to ${id ? 'update' : 'create'} popup`);
            }
        } catch (error) {
            console.error('Error saving popup:', error);
            alert(error.message || `Failed to ${id ? 'update' : 'create'} popup`);
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="container-xxl">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle={id ? "Edit Popup" : "Create Popup"} sidebutton={false} />

            <div className="row">
                <div className="col-xl-12">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <Card.Title className="mb-0">{id ? "Edit Popup" : "Create New Popup"}</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col lg={8}>
                                        <Card className="h-100 border-0 shadow-sm">
                                            <Card.Header className="bg-light">
                                                <h5 className="mb-0">Popup Content</h5>
                                            </Card.Header>
                                            <Card.Body>
                                                <Form.Group className="mb-4">
                                                    <Form.Label>
                                                        <strong>Title</strong>
                                                        <span className="text-danger ms-1">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="title"
                                                        value={formData.title}
                                                        onChange={handleInputChange}
                                                        required
                                                        placeholder="Enter popup title"
                                                    />
                                                </Form.Group>

                                                <Form.Group className="mb-4">
                                                    <Form.Label>
                                                        <strong>Content</strong>
                                                        <span className="text-danger ms-1">*</span>
                                                    </Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={10}
                                                        name="content"
                                                        value={formData.content}
                                                        onChange={handleInputChange}
                                                        required
                                                        placeholder="Enter popup content (HTML supported)"
                                                        style={{ minHeight: '200px' }}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        You can use HTML tags for formatting
                                                    </Form.Text>
                                                </Form.Group>
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    <Col lg={4}>
                                        <Card className="h-100 border-0 shadow-sm">
                                            <Card.Header className="bg-light">
                                                <h5 className="mb-0">Settings</h5>
                                            </Card.Header>
                                            <Card.Body>
                                                <Form.Group className="mb-4">
                                                    <Form.Label>
                                                        <strong>Status</strong>
                                                    </Form.Label>
                                                    <Form.Select
                                                        name="status"
                                                        value={formData.status}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </Form.Select>
                                                </Form.Group>

                                                <Form.Group className="mb-4">
                                                    <Form.Label>
                                                        <strong>Image</strong>
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                    />
                                                    {imagePreview && (
                                                        <div className="mt-3 position-relative d-inline-block">
                                                            <img
                                                                src={imagePreview}
                                                                alt="Preview"
                                                                className="img-fluid rounded"
                                                                style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                                                                onError={(e) => {
                                                                    console.error('Image load error:', imagePreview);
                                                                }}
                                                            />
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                className="position-absolute top-0 end-0 m-1"
                                                                onClick={handleRemoveImage}
                                                                title="Remove Image"
                                                            >
                                                                <i className="fa fa-times"></i>
                                                            </Button>
                                                        </div>
                                                    )}
                                                </Form.Group>

                                                <Form.Group className="mb-4">
                                                    <Form.Label>
                                                        <strong>Target Users</strong>
                                                    </Form.Label>
                                                    <Form.Select
                                                        name="targetUsers"
                                                        value={formData.targetUsers}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="all">All Users</option>
                                                        <option value="specific">Specific Users</option>
                                                    </Form.Select>
                                                </Form.Group>

                                                {formData.targetUsers === 'specific' && (
                                                    <Form.Group className="mb-4">
                                                        <Form.Label>
                                                            <strong>Select Users</strong>
                                                            <span className="text-danger ms-1">*</span>
                                                        </Form.Label>
                                                        <div 
                                                            style={{ 
                                                                maxHeight: '200px', 
                                                                overflowY: 'auto',
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: '0.375rem',
                                                                padding: '10px'
                                                            }}
                                                        >
                                                            {users.length === 0 ? (
                                                                <div className="text-muted">Loading users...</div>
                                                            ) : (
                                                                users.map((user) => (
                                                                    <Form.Check
                                                                        key={user._id}
                                                                        type="checkbox"
                                                                        id={`user-${user._id}`}
                                                                        label={`${user.name || user.email} ${user.mobileNumber ? `(${user.mobileNumber})` : ''}`}
                                                                        checked={formData.userIds.includes(user._id)}
                                                                        onChange={() => handleUserToggle(user._id)}
                                                                    />
                                                                ))
                                                            )}
                                                        </div>
                                                        {formData.userIds.length > 0 && (
                                                            <Form.Text className="text-muted">
                                                                {formData.userIds.length} user(s) selected
                                                            </Form.Text>
                                                        )}
                                                    </Form.Group>
                                                )}

                                                <div className="date-range-group mb-4 p-3 bg-light rounded">
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>
                                                            <strong>Display From</strong>
                                                            <span className="text-danger ms-1">*</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            name="displayFrom"
                                                            value={formData.displayFrom}
                                                            onChange={handleInputChange}
                                                            required
                                                        />
                                                    </Form.Group>

                                                    <Form.Group>
                                                        <Form.Label>
                                                            <strong>Display Until</strong>
                                                            <span className="text-muted ms-2">(Optional)</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            name="displayUntil"
                                                            value={formData.displayUntil}
                                                            onChange={handleInputChange}
                                                        />
                                                    </Form.Group>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>

                                <div className="mt-4">
                                    <Button
                                        variant="secondary"
                                        className="me-2"
                                        onClick={() => navigate('/admin/page-popups')}
                                        disabled={processing}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={processing}
                                    >
                                        {processing ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {id ? 'Updating...' : 'Creating...'}
                                            </>
                                        ) : (
                                            id ? 'Update Popup' : 'Create Popup'
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default PopupForm;

